import { SubscriptionPlanDropdown } from '../../components/SubscriptionPlanDropdown';
import { TextField } from '@/shared/components/TextField';
import CreateLayout from '@/shared/layout/createLayout';
import ErrorPage403 from '@/app/routes/errorPage403';
import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePickerField } from '@/shared/components/DatePickerField';
import Loader from '@/app/routes/Loader';
import { useEditOffer } from '../../hooks';
import { ErrorIcon } from '@/shared/components/TextField/TextField';
import { ToggleButton } from '@/shared/components/ToggleButton';
import { Tooltip } from '@/shared/components/Tooltip';
import { OfferFormData, OfferFormErrors } from '../../types/offerFormTypes';
import { OfferUpdateStatusDialog } from '../../components/OfferUpdateStatusDialog';
import { updateOfferStatusApiService } from '../../api/updateOfferStatusApiService';
import { Snackbar } from '@shared/components/Snackbar';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import { OFFER_ERROR_MESSAGES } from '../../errors/errorMessages';

interface EditOfferFormProps {
  formData: OfferFormData;
  setFormData: Dispatch<SetStateAction<OfferFormData>>;
  errors: OfferFormErrors;
  setErrors: Dispatch<SetStateAction<OfferFormErrors>>;
  selectedPlans: string[];
  setSelectedPlans: Dispatch<SetStateAction<string[]>>;
  startDate: Date | undefined;
  setStartDate: Dispatch<SetStateAction<Date | undefined>>;
  endDate: Date | undefined;
  setEndDate: Dispatch<SetStateAction<Date | undefined>>;
  isSubmitting: boolean;
  hasAvailablePlans: boolean | null;
  onHasItemsChange: (has: boolean) => void;
  onNavigateToCreateSubscription: () => void;
}

const EditOffer = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const {
    formData,
    setFormData,
    errors,
    setErrors,
    selectedPlans,
    setSelectedPlans,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    status,
    setStatus,
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    isLoading,
    accessError,
  } = useEditOffer(offerId || '');

  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();
  const isMissingActivateDeactivatePrivilege =
    !privileges.offerActivateDeactivate;

  const [hasAvailablePlans, setHasAvailablePlans] = useState<boolean | null>(
    null
  );

  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isStatusSubmittingLocal, setIsStatusSubmittingLocal] = useState(false);
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] = useState(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState('');

  const handleStatusDialogClose = () => {
    if (!isStatusSubmittingLocal) {
      setIsStatusDialogOpen(false);
    }
  };

  const handleConfirmStatusUpdate = async () => {
    if (!offerId) {
      return;
    }
    setIsStatusSubmittingLocal(true);
    const isActive = status !== 'active';
    try {
      const result = await updateOfferStatusApiService.updateOfferStatus({
        offerId,
        isActive,
      });
      if (result.success) {
        setStatus(isActive ? 'active' : 'inactive');
        setStatusSuccessMessage(
          isActive
            ? 'Oferta została aktywowana.'
            : 'Oferta została dezaktywowana.'
        );
        setShowStatusSuccessSnackbar(true);
        setIsStatusDialogOpen(false);
      } else {
        setStatusErrorMessage(
          result.error ?? 'Wystąpił błąd podczas aktualizacji statusu.'
        );
        setShowStatusErrorSnackbar(true);
        setIsStatusDialogOpen(false);
      }
    } catch {
      setStatusErrorMessage(
        'Wystąpił nieoczekiwany błąd podczas aktualizacji statusu.'
      );
      setShowStatusErrorSnackbar(true);
      setIsStatusDialogOpen(false);
    } finally {
      setIsStatusSubmittingLocal(false);
    }
  };

  const EditOfferHeader = () => {
    const disabled = isSubmitting || isLoading || isStatusSubmittingLocal;
    const statusToggle = (
      <ToggleButton
        checked={status === 'active'}
        onChange={() => {}}
        label={status === 'active' ? 'Aktywny' : 'Nieaktywny'}
        size="XSmall"
        disabled={disabled || isMissingActivateDeactivatePrivilege}
        data-testid="toggle-status"
      />
    );
    return (
      <div className="flex items-center justify-between">
        <p className="text-titleLarge text-neutral-10 ">Edytuj ofertę</p>
        <div
          className={`flex items-center gap-2${isMissingActivateDeactivatePrivilege ? ' opacity-50' : ''}`}
        >
          <p className="text-[12px] text-on-surface-variant">Status:</p>
          <div
            className="flex min-w-[110px] items-center"
            data-testid="offer-status-toggle"
          >
            <div className="leading-none">
              {isMissingActivateDeactivatePrivilege ? (
                <Tooltip
                  content={OFFER_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
                  side="left"
                  forceShow
                  triggerWrapContent
                >
                  {statusToggle}
                </Tooltip>
              ) : (
                <div
                  onClick={(e) => {
                    if (disabled) {
                      return;
                    }
                    e.stopPropagation();
                    setIsStatusDialogOpen(true);
                  }}
                  className={`flex min-w-[110px] items-center ${
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  <div className="leading-none pointer-events-none">
                    {statusToggle}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const EditOfferForm = useMemo(
    () =>
      function EditOfferForm({
        formData,
        setFormData,
        errors,
        setErrors,
        selectedPlans,
        setSelectedPlans,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        isSubmitting,
        hasAvailablePlans,
        onHasItemsChange,
        onNavigateToCreateSubscription,
      }: EditOfferFormProps) {
        return (
          <>
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Szczegóły oferty
              </p>
              <div className="flex w-full justify-between gap-4">
                <div className="flex flex-col gap-8 w-[68%] ">
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Imię i nazwisko"
                    maxLength={200}
                    autoFocus={true}
                    value={formData.offerName}
                    onChange={(e) => {
                      if (errors.offerName) {
                        setErrors((prev) => ({
                          ...prev,
                          offerName: '',
                        }));
                      }
                      const next = e.target.value.trimStart().slice(0, 200);
                      setFormData((prev) => ({
                        ...prev,
                        offerName: next,
                      }));
                    }}
                    error={!!errors.offerName}
                    errorMessage={errors.offerName ?? ''}
                    disabled={isSubmitting}
                    fullWidth={true}
                  />
                  <div className="flex flex-col gap-2">
                    <SubscriptionPlanDropdown
                      toolTip={false}
                      maxVisibleItems={7}
                      selectedIds={selectedPlans}
                      onSelectionChange={(ids) => {
                        setSelectedPlans(ids);
                        if (errors.selectedPlans) {
                          setErrors((prev) => ({
                            ...prev,
                            selectedPlans: '',
                          }));
                        }
                      }}
                      label={
                        <>
                          Plan subskrypcji{' '}
                          <span style={{ fontSize: 12 }}>(Opcjonalne)</span>
                        </>
                      }
                      disabled={isSubmitting}
                      error={!!errors.selectedPlans}
                      errorMessage={errors.selectedPlans ?? ''}
                      onHasItems={onHasItemsChange}
                    />
                    {hasAvailablePlans === false && (
                      <p className="text-bodySmall align-middle text-on-surface-variant">
                        No subscription plans available.{' '}
                        <span
                          className="text-titleSmall underline text-[#2A4455] cursor-pointer"
                          onClick={onNavigateToCreateSubscription}
                        >
                          Utwórz plan
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-[30%]">
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Identyfikator rabatu"
                    maxLength={200}
                    value={offerId || ''}
                    disabled={true}
                    fullWidth={true}
                  />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Konfiguracja oferty
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4 w-[68%]">
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Typ rabatu"
                    maxLength={200}
                    width={'50%'}
                    value={'Procentowy'}
                    onChange={(e) => {
                      if (errors.offerType) {
                        setErrors((prev) => ({
                          ...prev,
                          offerType: '',
                        }));
                      }
                      const next = e.target.value.trimStart().slice(0, 200);
                      setFormData((prev) => ({
                        ...prev,
                        offerType: next,
                      }));
                    }}
                    error={!!errors.offerType}
                    errorMessage={errors.offerType ?? ''}
                    disabled={isSubmitting}
                    readOnly={true}
                  />
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Wartość rabatu"
                    maxLength={200}
                    width={'50%'}
                    value={formData.discountValue}
                    onChange={(e) => {
                      let value = e.target.value;

                      // Allow only digits and one dot
                      value = value.replace(/[^0-9.]/g, '');

                      // Prevent multiple dots
                      const parts = value.split('.');
                      if (parts.length > 2) {
                        return;
                      }

                      // Limit leading zeros to max 4 (before decimal)
                      if (
                        parts[0] &&
                        parts[0].length > 4 &&
                        parts[0].startsWith('0')
                      ) {
                        return;
                      }

                      // Limit decimals to 2 places
                      if (parts[1] && parts[1].length > 2) {
                        return;
                      }

                      // Convert to number for range check
                      const numericValue = Number(value);

                      // Allow empty input while typing
                      if (value === '') {
                        setFormData((prev) => ({ ...prev, discountValue: '' }));
                        return;
                      }

                      // Restrict range 0–100
                      if (numericValue < 0 || numericValue > 100) {
                        return;
                      }

                      if (errors.discountValue) {
                        setErrors((prev) => ({
                          ...prev,
                          discountValue: '',
                        }));
                      }

                      setFormData((prev) => ({
                        ...prev,
                        discountValue: value,
                      }));
                    }}
                    error={!!errors.discountValue}
                    errorMessage={errors.discountValue ?? ''}
                    disabled={isSubmitting}
                    trailingIcon={
                      <div>
                        {errors.discountValue && (
                          <ErrorIcon className="w-6 h-6 mr-5" />
                        )}
                        <div
                          className="absolute top-0 bottom-0 right-0 flex items-center justify-center 
                                            border-l border-[#474A4C] px-4 rounded-r-lg text-[#181A1B] text-bodyLarge py-1 "
                        >
                          %
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Okres obowiązywania
              </p>
              <div className="flex flex-col gap-8 w-[33%]">
                <DatePickerField
                  mode="range"
                  startDate={startDate}
                  endDate={endDate}
                  onRangeChange={(start, end) => {
                    setStartDate(start);
                    setEndDate(end);
                    if (errors.startDate || errors.endDate) {
                      setErrors((prev) => ({
                        ...prev,
                        startDate: '',
                        endDate: '',
                      }));
                    }
                  }}
                  label="Zakres dat"
                  endYear={2100}
                  error={!!errors.startDate || !!errors.endDate}
                  errorMessage={errors.startDate || errors.endDate || ''}
                  minDate={new Date()}
                  placement="top-end"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </>
        );
      },
    [offerId]
  );

  if (!privilegesLoading && !privileges.offerEdit) {
    return <ErrorPage403 />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-full w-full">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={isSnackbarOpen}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError={snackbarError}
        Header={EditOfferHeader}
        submitButtonLabel="Zapisz zmiany"
        submitButtonLoadingLabel="Zapisywanie..."
      >
        <EditOfferForm
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          selectedPlans={selectedPlans}
          setSelectedPlans={setSelectedPlans}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          isSubmitting={isSubmitting}
          hasAvailablePlans={hasAvailablePlans}
          onHasItemsChange={setHasAvailablePlans}
          onNavigateToCreateSubscription={() => navigate('/Plany/Utwórz-plan')}
        />
      </CreateLayout>

      <Snackbar
        open={showStatusSuccessSnackbar}
        message={statusSuccessMessage}
        variant="success"
        onClose={() => setShowStatusSuccessSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showStatusErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={() => setShowStatusErrorSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />

      <OfferUpdateStatusDialog
        open={isStatusDialogOpen}
        onClose={handleStatusDialogClose}
        onConfirm={handleConfirmStatusUpdate}
        isSubmitting={isStatusSubmittingLocal}
        offerName={formData.offerName}
        isActive={status === 'active'}
      />
    </>
  );
};

export default EditOffer;
